import { forwardRef } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import classNames from '../../../utils/classNames';

const formRowClasses = 'mb-4 flex items-center justify-between gap-2 w-full';
const formLabelClasses = 'block text-sm w-24';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    name: string;
    error?: string;
    helperText?: string;
    formRowClasses?: string;
    isRequired?: boolean;
}

export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(
    (
        {
            label,
            error,
            name,
            helperText,
            formRowClasses: formRowClassesProps,
            isRequired,
            ...props
        },
        ref
    ) => {
        // Generate a unique, stable ID for this instance.
        const inputId = `form-input-${name}`;
        const helperTextId = `${inputId}-helper`;
        const errorTextId = `${inputId}-error`;

        // Prioritize error message for description; fallback to helper text.
        const describedBy = (error && errorTextId) || (helperText && helperTextId) || undefined;

        return (
            <div className={classNames(formRowClasses, formRowClassesProps)}>
                <label htmlFor={inputId} className={formLabelClasses}>
                    {label}
                    {/* Visual indicator for required state. */}
                    {isRequired && (
                        <span className="ml-1 text-red-600" aria-hidden="true">
                            *
                        </span>
                    )}
                </label>
                <div className="flex flex-1 flex-col gap-1">
                    <input
                        {...props}
                        ref={ref}
                        id={inputId}
                        name={name}
                        className={classNames(
                            'w-full max-w-64 rounded-lg border px-3 py-2 text-sm',
                            error && 'border-red-600 focus:border-red-600 focus:ring-red-600',
                            props.className
                        )}
                        /* Accessibility & Validation:
                         * 1. Disable native `required` to avoid browser popups.
                         * 2. Use `aria-required` & `aria-invalid` for screen readers.
                         * 3. Link errors/helpers via `describedBy`.
                         */
                        required={false}
                        aria-required={isRequired}
                        aria-invalid={!!error}
                        aria-describedby={describedBy}
                    />
                    {!error && helperText && (
                        <p id={helperTextId} className="text-xs text-gray-500">
                            {helperText}
                        </p>
                    )}
                    {error && (
                        <p
                            id={errorTextId}
                            className="text-xs text-red-600"
                            // Interrupts screen reader immediately.
                            role="alert"
                        >
                            {error}
                        </p>
                    )}
                </div>
            </div>
        );
    }
);

TextField.displayName = 'TextField';
